import React from 'react';

import { useClassnames } from '@lumapps/classnames';
import { Alignment, FlexBox, Orientation, RadioButton, RadioButtonProps, Theme } from '@lumapps/lumx/react';

import './index.scss';

export const CLASSNAME = 'lumx-expandable-radio-button';

export interface ExpandableRadioButtonProps extends Partial<RadioButtonProps> {
    /** which HTML element will be used for displaying the component */
    as?: React.ElementType;
    /** Optional content to display when the radio is checked */
    children?: React.ReactNode;
    /** Custom class name */
    className?: string;
    /** Component theme
     *
     * @default 'light'
     */
    theme?: Theme;
}

/**
 * Small component that can open a sub-section wheen the radio button is checked
 *
 * @family Layouts
 * @param ExpendableRadioButtonProps
 * @returns ExpendableRadioButton
 */
export const ExpandableRadioButton: React.FC<ExpandableRadioButtonProps> = ({
    className,
    as: Component = 'li',
    label,
    value,
    name,
    helper,
    onChange,
    isChecked = false,
    children,
    theme = Theme.light,
    ...props
}) => {
    const { block, element } = useClassnames(CLASSNAME);
    const CHILD_ID = `${name}--sub-section`;
    const shouldShowChildren = isChecked && children;

    return (
        <Component className={block(CLASSNAME, className)}>
            <FlexBox hAlign={Alignment.center} orientation={Orientation.horizontal} vAlign={Alignment.spaceBetween}>
                <RadioButton
                    label={label}
                    value={value}
                    name={name}
                    helper={helper}
                    onChange={onChange}
                    isChecked={isChecked}
                    {...props}
                />
            </FlexBox>

            {shouldShowChildren ? (
                <div className={element('sub-section', `theme-${theme}`)} id={CHILD_ID}>
                    {children}
                </div>
            ) : null}
        </Component>
    );
};
